import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { figma } from '../theme/figmaTokens';
import { SESSION_EXPIRED_MESSAGE_KEY } from '../services/api';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const resetMessage = (location.state as { resetMessage?: string } | null)?.resetMessage;

  useEffect(() => {
    try {
      const msg = sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_KEY);
      if (msg) {
        sessionStorage.removeItem(SESSION_EXPIRED_MESSAGE_KEY);
        setError(msg);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const validateUsername = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setUsernameError('');
      return false;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setUsernameError('Please enter a valid email format.');
      return false;
    }
    setUsernameError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateUsername(username)) {
      if (username.trim()) return;
    }
    if (!username.trim() || !password) {
      setError('Username and password are required.');
      return;
    }

    setLoading(true);

    try {
      await login(username.trim(), password);
      navigate('/select-station');
    } catch (err: any) {
      console.error('Login error in component:', err);
      const errorMessage = err.message || err.response?.data?.message || 'Login failed. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: figma.pageBg }}>
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              width: '100%',
              maxWidth: 440,
              backgroundColor: figma.cardBg,
              border: `1px solid ${figma.border}`,
              borderRadius: 1,
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/Quu_Logo_Gray.png`}
                alt="Quu"
                sx={{ height: 44, width: 'auto', mb: 2, mx: 'auto', display: 'block' }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  mb: 1,
                }}
              >
                HotQuus
              </Typography>
              <Typography variant="body1" sx={{ color: figma.textMuted, fontWeight: 500 }}>
                Log in using your Quu Credentials
              </Typography>
            </Box>

            {resetMessage && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => navigate('/login', { replace: true, state: {} })}>
                {resetMessage}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                label="Username"
                type="email"
                autoComplete="username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  if (usernameError) validateUsername(e.target.value);
                }}
                onBlur={() => validateUsername(username)}
                margin="normal"
                required
                disabled={loading}
                error={Boolean(usernameError)}
                helperText={usernameError}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: figma.white,
                    '&.Mui-focused': { color: figma.white },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: figma.white,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    '& fieldset': { borderColor: figma.border },
                    '&:hover fieldset': { borderColor: figma.textMutedLight },
                    '&.Mui-focused fieldset': { borderColor: figma.cyan, borderWidth: 2 },
                  },
                  '& .MuiFormHelperText-root': { color: '#f44336' },
                }}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    color: figma.white,
                    '&.Mui-focused': { color: figma.white },
                    '&.Mui-disabled': { color: 'rgba(255,255,255,0.5)' },
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: figma.white,
                    backgroundColor: 'rgba(255,255,255,0.08)',
                    '& fieldset': { borderColor: figma.border },
                    '&:hover fieldset': { borderColor: figma.textMutedLight },
                    '&.Mui-focused fieldset': { borderColor: figma.cyan, borderWidth: 2 },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  mt: 3,
                  mb: 1.5,
                  py: 1.25,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: '#ffffff',
                  backgroundColor: figma.cyan,
                  boxShadow: 'none',
                  '&:hover': {
                    color: '#ffffff',
                    backgroundColor: '#2d8fc4',
                    boxShadow: 'none',
                  },
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Sign In'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
